import { apiFetch } from './http';

/** One row of docs/verification/LEDGER.md, as the platform's copy of it reads. */
export interface LedgerRow {
  id: string;
  text: string;
  check: string;
  status: string;
  /** `people`: watched working by people, on the owner's word alone (the ledger's "seen by people"). */
  state: 'seen' | 'people' | 'failing' | 'unverified' | 'frozen';
}

export interface LedgerSection {
  key: string;
  title: string;
  rows: LedgerRow[];
}

export interface LedgerView {
  sections: LedgerSection[];
  counts: Record<string, number>;
  builtOn: string;
}

export const fetchLedger = () => apiFetch<LedgerView>('/api/status/ledger');

export { fetchTestEvidence, type TestEvidence } from './tests';

/** One check of a smoke night: what it asked, what it found, and the lines that decided it. */
export interface SmokeCheck {
  id: string;
  ledger: string;
  title: string;
  status: 'pass' | 'fail' | 'skip' | 'warn';
  evidence: string[];
}

export type SmokeResult = 'QUEUED' | 'RUNNING' | 'GREEN' | 'RED' | 'ABORTED';

export interface SmokeRunView {
  id: number;
  startedAt: string;
  finishedAt: string | null;
  panelServerId: number;
  worldClass: string;
  modSetHash: string | null;
  recipe: string;
  missionCode: string | null;
  sessionCode: string | null;
  candidateMod: string | null;
  commitSha: string | null;
  platformVersion: string | null;
  addonVersion: string | null;
  phase: string;
  result: SmokeResult;
  checks: SmokeCheck[];
  note: string | null;
  /** A queued night's place, 1 for the next to start; null for a night that is not queued. */
  position?: number | null;
  /** Why a queued night still waits, in the platform's own words. */
  waiting?: string | null;
}

export interface SmokeRequest {
  panelServerId: number;
  /** Not needed with `missionCode`: the mission's own terrain is the night's. */
  worldClass?: string | null;
  /** A stored mission to play instead of generating one (release D, D11.12). */
  missionCode?: string | null;
  /** Its revision to play; null is its current one. */
  revision?: number | null;
  modSetHash?: string | null;
  enemyProfileId?: number | null;
  recipeId?: string | null;
  players?: number | null;
  candidateMod?: string | null;
  commitSha?: string | null;
}

export const fetchSmokeRuns = () => apiFetch<SmokeRunView[]>('/api/smoke');
export const fetchSmokeRun = (id: number) => apiFetch<SmokeRunView>(`/api/smoke/${id}`);
export const startSmokeNight = (request: SmokeRequest) =>
  apiFetch<SmokeRunView>('/api/smoke', { method: 'POST', body: request });
export const abortSmokeNight = (id: number) =>
  apiFetch<SmokeRunView>(`/api/smoke/${id}/abort`, { method: 'POST' });

/** One part of a count: a recipe, and the people who generated a mission of it. */
export interface UsePart {
  label: string;
  people: number;
}

/** The administrator's word that something was seen in use. It carries no name: only whether it was his own. */
export interface UseAcceptance {
  acceptedAt: string;
  /** The platform version it was accepted under. */
  version: string;
  /** The people counted at that moment. */
  count: number;
  note: string | null;
  /** Accepted under another version than the one running. */
  stale: boolean;
  byYou: boolean;
}

/** One thing people do with the platform, and how many different people have done it. */
export interface UseEntry {
  id: string;
  label: string;
  /** The ledger row it supports, e.g. `A2`. */
  ledgerRow: string | null;
  /** The roadmap item it supports. */
  roadmapItem: string | null;
  /** Something only an administrator does: his own use is counted in `people`. */
  adminAction: boolean;
  /** Different people since the running version first started; the administrator asking is left out. */
  people: number;
  peopleEver: number;
  /** The administrator asking has done it himself since then. */
  mine: boolean;
  breakdown: UsePart[];
  acceptance: UseAcceptance | null;
}

export interface UseView {
  version: string;
  since: string;
  entries: UseEntry[];
}

export const fetchUse = () => apiFetch<UseView>('/api/status/use');
export const acceptUse = (id: string) =>
  apiFetch<UseEntry>(`/api/status/use/${id}/accept`, { method: 'POST' });
export const withdrawUse = (id: string) =>
  apiFetch<UseEntry>(`/api/status/use/${id}/accept`, { method: 'DELETE' });
